import { useState, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { primaryText, baseTextSize, secondaryText, tertiaryText, semiBoldText, xxLargeTextSize, smallTextSize } from '../../../styling/stylingHelper';
import { RecipeComponent, getTranslatedText, translateRecipe, SupportedLanguage, ALL_LANGUAGES } from '../../../services/recipes/recipesService';
import { useRecipeStore } from '../../../stores/appRecipeStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useLoadRecipeDetail, useLoadCostPrice } from '../../../utils/dataLoadingUtils';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessRecipeAutomation, canAccessRecipesPage } from '../../../services/accessManagement/accessManagement';
import { ArrowLeft, Edit, Trash2, Link2, Link2Off, ChefHat, Languages, Loader2 } from 'lucide-react';

// Extracted components
import {
  RecipeImageCarousel,
  RecipeAllergiesSection,
  RecipeDietsSection,
  RecipeComponentsSection,
} from './components';

// Modals
import RecipeNoteModal from '../../recipes/RecipeNoteModal';
import SaveRecipeModal from '../../recipes/SaveRecipeModal';
import ComponentEditorModal from '../../recipes/ComponentEditorModal';
import EditRecipeDishLinkModal from './modals/EditRecipeDishLinkModal';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';

const RecipeDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;

  // Check if user has access to recipe automation
  const hasRecipeAutomationAccess = canAccessRecipeAutomation(backendUser, accountSettings);

  // Use store
  const recipe = useRecipeStore((s) => s.selectedRecipe);
  const isLoading = useRecipeStore((s) => s.isLoadingDetail);
  const reloadRecipeDetail = useRecipeStore((s) => s.reloadRecipeDetail);
  const getNotesForComponent = useRecipeStore((s) => s.getNotesForComponent);
  const updateImages = useRecipeStore((s) => s.updateImages);
  const updateAllergenDeclaration = useRecipeStore((s) => s.updateAllergenDeclaration);
  const updateDiets = useRecipeStore((s) => s.updateDiets);
  const updateCostPriceLink = useRecipeStore((s) => s.updateCostPriceLink);
  const reorderComponents = useRecipeStore((s) => s.reorderComponents);
  const reorderSteps = useRecipeStore((s) => s.reorderSteps);
  const saveComponent = useRecipeStore((s) => s.saveComponent);
  const deleteComponent = useRecipeStore((s) => s.deleteComponent);
  const deleteNote = useRecipeStore((s) => s.deleteNote);
  const deleteRecipeData = useRecipeStore((s) => s.deleteRecipeData);

  // Load recipe detail via hook
  useLoadRecipeDetail(id);
  // The "Linked dish" picker lists the restaurant's cost price analyses, and the
  // badge resolves the linked one — both come from the cost price store.
  useLoadCostPrice();
  const costPriceList = useCostPriceStore((s) => s.costPriceList);

  // Link targets are this restaurant's analyses (the store can hold the
  // "All Restaurants" superset when the user came from that view).
  const restaurantAnalyses = useMemo(
    () => costPriceList.filter((a) => a.restaurantId === recipe?.restaurantId),
    [costPriceList, recipe?.restaurantId]
  );

  const linkedAnalysis = useMemo(
    () => (recipe?.costPriceId ? costPriceList.find((a) => a.id === recipe.costPriceId) : undefined),
    [costPriceList, recipe?.costPriceId]
  );

  // Prefer the linked analysis' own name; fall back to the mirrored (or legacy
  // name-join) revenue item so the badge is never blank while cost price loads.
  const linkedDishLabel = linkedAnalysis?.costPriceName || recipe?.menuItemName || null;

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Component editor state
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);
  const [componentEditMode, setComponentEditMode] = useState<'create' | 'edit'>('create');
  const [editingComponentIndex, setEditingComponentIndex] = useState<number | null>(null);
  const [editingComponent, setEditingComponent] = useState<RecipeComponent | undefined>(undefined);

  // Cost price link management state
  const [showEditDishLinkModal, setShowEditDishLinkModal] = useState(false);

  // Recipe notes state
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string>('');
  const [selectedComponentDisplayName, setSelectedComponentDisplayName] = useState<string>('');
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  // Translation state
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [translateSourceLang, setTranslateSourceLang] = useState<SupportedLanguage>('en');

  // Handlers
  const handleSaveRecipe = async () => {
    await reloadRecipeDetail();
    setIsEditModalOpen(false);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteRecipeData();
      navigate('/recipes');
    } catch (error) {
      console.error('Failed to delete recipe:', error);
      setDeleting(false);
    }
  };

  // Component management
  const handleAddComponent = () => {
    setComponentEditMode('create');
    setEditingComponentIndex(null);
    setEditingComponent(undefined);
    setIsComponentModalOpen(true);
  };

  const handleEditComponent = (index: number) => {
    if (!recipe) return;
    setComponentEditMode('edit');
    setEditingComponentIndex(index);
    setEditingComponent(recipe.components[index]);
    setIsComponentModalOpen(true);
  };

  const handleSaveComponent = async (component: RecipeComponent, imageFiles: File[]) => {
    try {
      await saveComponent(component, imageFiles, componentEditMode, editingComponentIndex);
      setIsComponentModalOpen(false);
    } catch (error) {
      console.error('Failed to save component:', error);
    }
  };

  const handleDeleteComponent = async (index: number) => {
    if (!window.confirm(t('recipes.confirmDeleteComponent'))) return;
    try {
      await deleteComponent(index);
    } catch (error) {
      console.error('Failed to delete component:', error);
    }
  };

  // Note handlers
  const handleAddNote = (componentId: string, componentDisplayName: string) => {
    setSelectedComponentId(componentId);
    setSelectedComponentDisplayName(componentDisplayName);
    setIsNoteModalOpen(true);
  };

  // Translation handler
  const handleTranslateAll = async () => {
    if (!recipe) return;
    setIsTranslating(true);
    try {
      const targetLanguages = ALL_LANGUAGES.filter(l => l !== translateSourceLang);
      await translateRecipe(recipe.id, translateSourceLang, targetLanguages);
      await reloadRecipeDetail();
      setShowTranslateModal(false);
    } catch (error) {
      console.error('Translation failed:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    setDeletingNoteId(noteId);
    try {
      await deleteNote(noteId);
    } catch (error) {
      console.error('Failed to delete note:', error);
    } finally {
      setDeletingNoteId(null);
    }
  };

  // Cost price link handler
  const handleSaveCostPriceLink = async (newCostPriceId: string | null) => {
    try {
      await updateCostPriceLink(newCostPriceId);
      setShowEditDishLinkModal(false);
    } catch (error) {
      console.error('Failed to update cost price link:', error);
      throw error;
    }
  };

  if (!canAccessRecipesPage(backendUser, accountSettings)) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#0E8965]/20 dark:border-[#0E8965]/20 border-t-[#0E8965] dark:border-t-[#0E8965] rounded-full animate-spin" />
          <p className={cn(baseTextSize, tertiaryText)}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-full bg-[#F2F4F7] dark:bg-zinc-800 flex items-center justify-center mb-4">
          <ChefHat className="w-10 h-10 text-[#667085] dark:text-textTertiary-dark" />
        </div>
        <p className={cn(baseTextSize, semiBoldText, primaryText, 'mb-2')}>
          {t('recipes.recipeNotFound')}
        </p>
        <button
          onClick={() => navigate('/recipes')}
          className={cn(
            "mt-4 flex items-center gap-2 px-4 py-2.5 rounded-xl",
            "border border-[#D0D5DD] dark:border-borderPrimary-dark",
            "text-[#344054] dark:text-textSecondary-dark font-semibold",
            "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors"
          )}
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('recipes.backToRecipes')}</span>
        </button>
      </div>
    );
  }

  return (
    <div>
      <MobilePageHeader title={getTranslatedText(recipe.nameTranslations, lang)} />
      {/* Header */}
      <div className="w-full flex flex-col gap-4 min-[1050px]:flex-row min-[1050px]:justify-between min-[1050px]:items-start relative pb-6">
        {/* Left side: Back button and title */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => navigate('/recipes')}
            className={cn(
              "flex items-center gap-2 mb-3",
              "text-[14px] leading-5 font-medium",
              "text-[#475467] dark:text-textSecondary-dark",
              "hover:text-[#0E8965] dark:hover:text-[#0E8965] transition-colors"
            )}
          >
            <ArrowLeft className="w-4 h-4" />
            {t('recipes.backToRecipes')}
          </button>

          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'mb-3', 'hidden lg:block')}>
            {getTranslatedText(recipe.nameTranslations, lang)}
          </h1>

          {/* Linked dish badge — shows the linked cost price item, plus the
              revenue item it derives its sales data from. Falls back to the
              legacy menuItemName-only link for recipes not migrated yet. */}
          <button
            type="button"
            onClick={() => setShowEditDishLinkModal(true)}
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors",
              linkedDishLabel
                ? "bg-[#ECFDF3] dark:bg-green-900/20 text-[#027A48] dark:text-green-400 hover:bg-[#D1FADF] dark:hover:bg-green-800/30"
                : "bg-[#F2F4F7] dark:bg-zinc-800 text-[#475467] dark:text-textTertiary-dark hover:bg-[#E4E7EC] dark:hover:bg-zinc-700"
            )}
          >
            {linkedDishLabel ? (
              <>
                <Link2 className="w-4 h-4" />
                <span className="text-sm font-medium">{linkedDishLabel}</span>
                {recipe.menuItemName && recipe.menuItemName !== linkedDishLabel && (
                  <span className="text-sm">
                    {t('recipes.salesItem', { name: recipe.menuItemName })}
                  </span>
                )}
              </>
            ) : (
              <>
                <Link2Off className="w-4 h-4" />
                <span className="text-sm font-medium">{t('recipes.noLinkedDish')}</span>
              </>
            )}
          </button>
        </div>

        {/* Right side: Actions */}
        <div className="flex items-center gap-2">
          {/* Translate All button */}
          <button
            onClick={() => setShowTranslateModal(true)}
            className={cn(
              "flex items-center gap-2 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark",
              "bg-white dark:bg-background-dark px-3 py-[11px]",
              "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors"
            )}
          >
            <Languages className="w-4 h-4 text-[#344054] dark:text-textSecondary-dark" />
            <span className="hidden min-[650px]:inline text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark">
              {t('recipes.translateAll')}
            </span>
          </button>

          <button
            onClick={() => setIsEditModalOpen(true)}
            className={cn(
              "flex items-center gap-2 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark",
              "bg-white dark:bg-background-dark px-3 py-[11px]",
              "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors"
            )}
          >
            <Edit className="w-4 h-4 text-[#344054] dark:text-textSecondary-dark" />
            <span className="hidden min-[650px]:inline text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark">
              {t('common.edit')}
            </span>
          </button>

          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className={cn(
              "flex items-center gap-2 rounded-xl",
              "bg-[#FEF3F2] dark:bg-red-900/20 px-3 py-[11px]",
              "hover:bg-[#FEE4E2] dark:hover:bg-red-900/30 transition-colors"
            )}
          >
            <Trash2 className="w-4 h-4 text-[#B42318] dark:text-red-400" />
            <span className="hidden min-[650px]:inline text-[14px] leading-5 font-semibold text-[#B42318] dark:text-red-400">
              {t('common.delete')}
            </span>
          </button>
        </div>
      </div>

      {/* Image Carousel */}
      <RecipeImageCarousel
        recipe={recipe}
        onSaveImages={updateImages}
      />

      {/* Info Cards Row */}
      <div className="grid grid-cols-1 min-[650px]:grid-cols-2 gap-4 mb-6">
        <RecipeAllergiesSection
          key={recipe.id}
          recipe={recipe}
          onSave={updateAllergenDeclaration}
          onReload={reloadRecipeDetail}
        />

        <RecipeDietsSection
          diets={recipe.diets || []}
          onSave={updateDiets}
        />
      </div>

      {/* Components Section */}
      <RecipeComponentsSection
        components={recipe.components}
        getNotesForComponent={getNotesForComponent}
        currentUserId={backendUser?.id || ''}
        currentUserRole={(backendUser?.role as 'admin' | 'staff') || 'staff'}
        deletingNoteId={deletingNoteId}
        onReorderComponents={reorderComponents}
        onReorderSteps={reorderSteps}
        onAddComponent={handleAddComponent}
        onEditComponent={handleEditComponent}
        onDeleteComponent={handleDeleteComponent}
        onAddNote={handleAddNote}
        onDeleteNote={handleDeleteNote}
      />

      {/* Edit Recipe Modal */}
      <SaveRecipeModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mode="edit"
        restaurantId={recipe.restaurantId}
        recipe={recipe}
        onSave={handleSaveRecipe}
      />

      {/* Component Editor Modal */}
      <ComponentEditorModal
        isOpen={isComponentModalOpen}
        onClose={() => setIsComponentModalOpen(false)}
        mode={componentEditMode}
        component={editingComponent}
        onSave={handleSaveComponent}
        hasRecipeAutomationAccess={hasRecipeAutomationAccess}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50"
          onClick={() => setIsDeleteModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-background-dark rounded-2xl shadow-2xl w-full max-w-md border border-[#E5E7EB] dark:border-borderPrimary-dark"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Icon */}
              <div className="w-12 h-12 rounded-full bg-[#FEF3F2] dark:bg-red-900/20 flex items-center justify-center mb-4 mx-auto">
                <Trash2 className="w-6 h-6 text-[#B42318] dark:text-red-400" />
              </div>

              <h2 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'text-center mb-2')}>
                {t('recipes.confirmDelete')}
              </h2>
              <p className={cn(baseTextSize, secondaryText, 'text-center mb-6')}>
                {t('recipes.confirmDeleteMessage', { name: getTranslatedText(recipe.nameTranslations, lang) })}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={deleting}
                  className={cn(
                    "flex-1 px-4 py-2.5 rounded-xl",
                    "border border-[#D0D5DD] dark:border-borderPrimary-dark",
                    "text-[14px] leading-5 font-semibold",
                    "text-[#344054] dark:text-textSecondary-dark",
                    "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors",
                    "disabled:opacity-50"
                  )}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className={cn(
                    "flex-1 px-4 py-2.5 rounded-xl",
                    "bg-[#D92D20] dark:bg-[#D92D20] text-white dark:text-white",
                    "text-[14px] leading-5 font-semibold",
                    "hover:bg-[#B42318] transition-colors",
                    "disabled:opacity-50"
                  )}
                >
                  {deleting ? t('common.deleting') : t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Linked Dish Modal */}
      {showEditDishLinkModal && (
        <EditRecipeDishLinkModal
          currentCostPriceId={recipe.costPriceId ?? null}
          analyses={restaurantAnalyses}
          onSave={handleSaveCostPriceLink}
          onClose={() => setShowEditDishLinkModal(false)}
        />
      )}

      {/* Recipe Note Modal */}
      <RecipeNoteModal
        isOpen={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        recipeId={recipe.id}
        componentId={selectedComponentId}
        componentDisplayName={selectedComponentDisplayName}
        restaurantId={recipe.restaurantId}
        onSave={reloadRecipeDetail}
      />

      {/* Translate All Modal */}
      {showTranslateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50"
          onClick={() => setShowTranslateModal(false)}
        >
          <div
            className="bg-white dark:bg-background-dark rounded-2xl shadow-2xl w-full max-w-md border border-[#E5E7EB] dark:border-borderPrimary-dark"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'mb-2')}>
                {t('recipes.translateAll')}
              </h2>
              <p className={cn(baseTextSize, secondaryText, 'mb-6')}>
                {t('recipes.translateAllDescription')}
              </p>

              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.sourceLanguage')}
              </label>
              <select
                value={translateSourceLang}
                onChange={(e) => setTranslateSourceLang(e.target.value as SupportedLanguage)}
                className={cn(
                  "w-full px-4 py-2.5 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark",
                  "bg-white dark:bg-background-dark",
                  "text-[14px] leading-5 text-[#344054] dark:text-textSecondary-dark",
                  "focus:outline-none focus:ring-2 focus:ring-[#0E8965]/20 dark:focus:ring-[#0E8965]/20 focus:border-[#0E8965] mb-4"
                )}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="nl">Nederlands</option>
              </select>

              <p className={cn(smallTextSize, tertiaryText, 'mb-6')}>
                {t('recipes.willTranslateTo', {
                  languages: ALL_LANGUAGES
                    .filter(l => l !== translateSourceLang)
                    .map(l => t(`recipes.languageTabs.${l}`))
                    .join(` ${t('common.and')} `)
                })}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowTranslateModal(false)}
                  disabled={isTranslating}
                  className={cn(
                    "flex-1 px-4 py-2.5 rounded-xl",
                    "border border-[#D0D5DD] dark:border-borderPrimary-dark",
                    "text-[14px] leading-5 font-semibold",
                    "text-[#344054] dark:text-textSecondary-dark",
                    "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors",
                    "disabled:opacity-50"
                  )}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleTranslateAll}
                  disabled={isTranslating}
                  className={cn(
                    "flex-1 px-4 py-2.5 rounded-xl",
                    "bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white",
                    "text-[14px] leading-5 font-semibold",
                    "hover:bg-[#047857] transition-colors",
                    "disabled:opacity-50 flex items-center justify-center gap-2"
                  )}
                >
                  {isTranslating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('recipes.translating')}
                    </>
                  ) : (
                    t('recipes.translateAll')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeDetailPage;
